import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Brain, RefreshCw } from './icons';
import { useAuth } from '../contexts/AuthContext';
import { getUserAvailableRoles, activatePartnerCoachAccount } from '../lib/auth';
import type { UserRole } from '../types/auth';

interface RoleSwitcherProps {
  currentRole: 'partner' | 'coach';
}

export function RoleSwitcher({ currentRole }: RoleSwitcherProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  const [isActivating, setIsActivating] = useState(false);
  const [showActivate, setShowActivate] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    getUserAvailableRoles(user.id).then(setAvailableRoles);
  }, [user?.id]);

  const hasDualRole = availableRoles.includes('partner') && availableRoles.includes('coach');
  const canActivateCoach = currentRole === 'partner' && availableRoles.includes('partner') && !availableRoles.includes('coach');

  const handleSwitch = () => {
    if (currentRole === 'partner') {
      navigate('/coach');
    } else {
      navigate('/partner');
    }
  };

  const handleActivateCoach = async () => {
    if (!user?.id) return;
    setIsActivating(true);
    try {
      await activatePartnerCoachAccount(user.id);
      const roles = await getUserAvailableRoles(user.id);
      setAvailableRoles(roles);
      setShowActivate(false);
      navigate('/coach');
    } catch (err) {
      console.error('Failed to activate coach account:', err);
    } finally {
      setIsActivating(false);
    }
  };

  if (hasDualRole) {
    const targetRole = currentRole === 'partner' ? 'coach' : 'partner';
    const TargetIcon = targetRole === 'coach' ? Brain : Users;

    return (
      <motion.button
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={handleSwitch}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-sand-50 to-sand-100 border border-sand-200 hover:border-sand-300 hover:shadow-sm transition-all group"
      >
        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow transition-shadow">
          <TargetIcon className="w-4 h-4 text-chocolate-600" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-xs text-sand-500 font-medium">Switch to</div>
          <div className="text-sm font-semibold text-chocolate-800 capitalize">{targetRole} View</div>
        </div>
        <RefreshCw className="w-4 h-4 text-sand-400 group-hover:text-chocolate-500 transition-colors" />
      </motion.button>
    );
  }

  if (canActivateCoach) {
    return (
      <div className="space-y-2">
        {!showActivate ? (
          <button
            onClick={() => setShowActivate(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-sand-50 border border-dashed border-sand-300 hover:border-sand-400 hover:bg-sand-100 transition-all text-left"
          >
            <div className="p-2 bg-white rounded-lg">
              <Brain className="w-4 h-4 text-sand-500" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-chocolate-700">Activate Coach Account</div>
              <div className="text-xs text-sand-500">Use your own coach tools</div>
            </div>
          </button>
        ) : (
          <div className="p-4 rounded-xl bg-white border border-sand-200 space-y-3">
            <p className="text-sm text-chocolate-700">
              This creates a coach account linked to your partner profile, giving you access to all coach features under the same login.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleActivateCoach}
                disabled={isActivating}
                className="flex-1 px-3 py-2 rounded-lg bg-chocolate-800 text-white text-sm font-medium hover:bg-chocolate-700 disabled:opacity-50 transition-colors"
              >
                {isActivating ? 'Activating...' : 'Activate'}
              </button>
              <button
                onClick={() => setShowActivate(false)}
                className="px-3 py-2 rounded-lg bg-sand-100 text-chocolate-600 text-sm font-medium hover:bg-sand-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
